// frontend/src/components/TemplateEditModals.js
// Componente que contiene los formularios modales para editar plantillas.
// CORREGIDO: Inicialización de estado robusta para prevenir errores con props nulas.

import React, { useState, useEffect } from 'react';
import { useUIStore } from '../store/uiStore';
import LoadingSpinner from './LoadingSpinner';
import { XMarkIcon } from '@heroicons/react/24/solid';

// --- Componente Base del Modal ---
function Modal({ title, children, onClose, show }) {
    if (!show) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4 border-b pb-3">
                    <h3 className="text-xl font-bold text-gray-900">{title}</h3>
                    <button onClick={onClose} className="p-1 rounded-full text-gray-500 hover:bg-gray-200">
                        <XMarkIcon className="h-6 w-6" />
                    </button>
                </div>
                <div>{children}</div>
            </div>
        </div>
    );
}

// --- Modal para Fases ---
export function PhaseModal({ show, onClose, onSubmit, initialData }) {
    const [formData, setFormData] = useState(initialData || { name: '', order: 0 });
    const { isLoading } = useUIStore();

    useEffect(() => {
        setFormData(initialData || { name: '', order: 0 });
    }, [initialData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit(formData);
    };

    return (
        <Modal title={initialData?.id ? 'Editar Fase' : 'Añadir Nueva Fase'} show={show} onClose={onClose}>
            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">Nombre de la Fase</label>
                    <input type="text" name="name" id="name" value={formData.name} onChange={handleChange} required className="mt-1 w-full border-gray-300 rounded-md shadow-sm"/>
                </div>
                <div>
                    <label htmlFor="order" className="block text-sm font-medium text-gray-700">Orden</label>
                    <input type="number" name="order" id="order" value={formData.order} onChange={handleChange} required className="mt-1 w-full border-gray-300 rounded-md shadow-sm"/>
                </div>
                <div className="pt-4 flex justify-end space-x-2">
                    <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">Cancelar</button>
                    <button type="submit" disabled={isLoading} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300 flex items-center justify-center">
                        {isLoading ? <LoadingSpinner size="sm" /> : 'Guardar'}
                    </button>
                </div>
            </form>
        </Modal>
    );
}

// --- Modal para Tareas ---
export function TaskModal({ show, onClose, onSubmit, initialData }) {
    const [formData, setFormData] = useState(initialData || { description: '', order: 0 });
    const { isLoading } = useUIStore();

    useEffect(() => {
        setFormData(initialData || { description: '', order: 0 });
    }, [initialData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit(formData);
    };

    return (
        <Modal title={initialData?.id ? 'Editar Tarea' : 'Añadir Nueva Tarea'} show={show} onClose={onClose}>
            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700">Descripción de la Tarea</label>
                    <textarea name="description" id="description" value={formData.description} onChange={handleChange} required rows="3" className="mt-1 w-full border-gray-300 rounded-md shadow-sm"></textarea>
                </div>
                <div>
                    <label htmlFor="order" className="block text-sm font-medium text-gray-700">Orden</label>
                    <input type="number" name="order" id="order" value={formData.order} onChange={handleChange} required className="mt-1 w-full border-gray-300 rounded-md shadow-sm"/>
                </div>
                <div className="pt-4 flex justify-end space-x-2">
                    <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">Cancelar</button>
                    <button type="submit" disabled={isLoading} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300 flex items-center justify-center">
                        {isLoading ? <LoadingSpinner size="sm" /> : 'Guardar'}
                    </button>
                </div>
            </form>
        </Modal>
    );
}

// --- Modal para Documentos ---
export function DocumentSlotModal({ show, onClose, onSubmit, phases, initialData }) {
    const [formData, setFormData] = useState(initialData || { name: '', key: '', order: 0, templatePhaseId: null });
    const { isLoading } = useUIStore();

    useEffect(() => {
        setFormData(initialData || { name: '', key: '', order: 0, templatePhaseId: null });
    }, [initialData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'name' && !initialData?.id) { // Solo autogenerar key al crear
            const key = value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
            setFormData(prev => ({ ...prev, name: value, key }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const dataToSubmit = { ...formData, templatePhaseId: formData.templatePhaseId === 'general' ? null : formData.templatePhaseId };
        onSubmit(dataToSubmit);
    };

    return (
        <Modal title={initialData?.id ? 'Editar Documento' : 'Añadir Nuevo Documento'} show={show} onClose={onClose}>
            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">Nombre del Documento (Visible para el usuario)</label>
                    <input type="text" name="name" id="name" value={formData.name} onChange={handleChange} required className="mt-1 w-full border-gray-300 rounded-md shadow-sm"/>
                </div>
                <div>
                    <label htmlFor="key" className="block text-sm font-medium text-gray-700">Clave Interna (No editable)</label>
                    <input type="text" name="key" id="key" value={formData.key} readOnly className="mt-1 w-full border-gray-300 rounded-md shadow-sm bg-gray-100"/>
                </div>
                <div>
                    <label htmlFor="templatePhaseId" className="block text-sm font-medium text-gray-700">Agrupar en Fase (Opcional)</label>
                    <select name="templatePhaseId" id="templatePhaseId" value={formData.templatePhaseId || 'general'} onChange={handleChange} className="mt-1 w-full border-gray-300 rounded-md shadow-sm">
                        <option value="general">General (Sin fase)</option>
                        {phases.map(phase => (
                            <option key={phase.id} value={phase.id}>{phase.name}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label htmlFor="order" className="block text-sm font-medium text-gray-700">Orden</label>
                    <input type="number" name="order" id="order" value={formData.order} onChange={handleChange} required className="mt-1 w-full border-gray-300 rounded-md shadow-sm"/>
                </div>
                <div className="pt-4 flex justify-end space-x-2">
                    <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">Cancelar</button>
                    <button type="submit" disabled={isLoading} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300 flex items-center justify-center">
                        {isLoading ? <LoadingSpinner size="sm" /> : 'Guardar'}
                    </button>
                </div>
            </form>
        </Modal>
    );
}
